/**
 * Copyright (c) 2026 Lars Jänsch. All Rights Reserved.
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 * See LICENSE file for terms and conditions.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
// import App from './App.test'; // TEMP: Minimal Test
import './index.css';
import './styles/tokens.css';
import './styles/bridge/legacy.css';
import './styles/bridge/dark-legacy.css';
import './styles/bridge/dark.css';
import { applyAppearance } from './utils/appearance';

applyAppearance();

// IndexedDB initialisieren
import { initDatabase } from './services/dbService';
import { startAutoSync } from './services/syncService';

// App-Version (MAJOR.MINOR.PATCH)
// Kommt beim Build aus backend/package.json (siehe vite.config.js)
const APP_VERSION = __APP_VERSION__;
window.APP_VERSION = APP_VERSION;

// Global Fetch Interceptor to attach App Version to all API requests
const originalFetch = window.fetch;
window.fetch = function (input, init) {
  let url = '';
  if (typeof input === 'string') {
    url = input;
  } else if (input && input.url) {
    url = input.url;
  }

  // Check if it goes to our API (either absolute or relative containing /api/ or starting with /api)
  if (url.includes('/api/') || url.startsWith('/api')) {
    init = init || {};
    let headers = init.headers || {};
    if (headers instanceof Headers) {
      headers.set('X-App-Version', APP_VERSION);
    } else if (Array.isArray(headers)) {
      headers.push(['X-App-Version', APP_VERSION]);
    } else {
      headers = { ...headers, 'X-App-Version': APP_VERSION };
    }
    init.headers = headers;
  }
  return originalFetch.call(this, input, init);
};

// PWA Service Worker
// In dev mode: deregister any stale SW (e.g. from a previous production build)
// that would otherwise intercept Vite dev-server requests and serve stale chunks,
// causing multiple-React-instance crashes when the device goes offline.
if (import.meta.env.DEV) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const reg of registrations) {
        reg.unregister();
        console.log('[dev] Service worker unregistered:', reg.scope);
      }
    });
  }
} else {
  // Production only – register the real offline service worker
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      onNeedRefresh() {
        console.log('New content available, please refresh.');
      },
      onOfflineReady() {
        console.log('App ready to work offline');
      },
    });
  });
}

// Datenbank im Hintergrund initialisieren
initDatabase()
  .then(() => {
    console.log('Database initialized');

    // Starte automatische Hintergrund-Synchronisation
    // - Prüft ob Daten älter als 24h
    // - Lädt Daten und Bilder automatisch
    // - Läuft komplett im Hintergrund
    startAutoSync().catch(error => {
      console.warn('Auto-sync failed:', error);
      // Fehler nicht kritisch, App funktioniert trotzdem
    });
  })
  .catch(error => {
    console.error('Database init failed:', error);
  });

// App sofort rendern
ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
